import React from 'react';
import Checkbox from '@srm/ui/checkbox';
import FormGroup from '@srm/ui/form-group';
import Input from '@srm/ui/input';
import Select from '@srm/ui/select';
import Textarea from '@srm/ui/textarea';
import Hint from '@srm/ui/hint';
import { IMatrixComplianceAnswer } from '../WinnerSelection2.types';
import { COMPLIANCE_LABEL } from '../utils/matrixView';
import { commentRequiredFor, supplierOffersHint } from '../utils/dialogForm';

interface IQuantityFieldProps {
    value: string;
    unit: string | null;
    disabled?: boolean;
    invalid?: boolean;
    errorMessage?: string;
    onChange: (value: string) => void;
}

export const QuantityField: React.FC<IQuantityFieldProps> = ({
    value,
    unit,
    disabled,
    invalid,
    errorMessage,
    onChange,
}) => (
    <FormGroup
        label={unit ? `Выбранное количество, ${unit}` : 'Выбранное количество'}
        invalid={invalid}
        errorMessage={errorMessage}
    >
        {/* Не InputNumber: при дробной точности он дополняет значение нулями (100.000000) */}
        <Input
            value={value}
            inputMode="decimal"
            pattern="^[0-9]*[.,]?[0-9]*$"
            disabled={disabled}
            invalid={invalid}
            onChange={(event) => onChange(event.target.value)}
            aria-label="Выбранное количество"
        />
    </FormGroup>
);

interface IComplianceFieldsProps {
    answers: IMatrixComplianceAnswer[];
    answer: string;
    comment: string;
    disabled?: boolean;
    commentError?: string;
    onAnswer: (value: string) => void;
    onComment: (value: string) => void;
}

const NO_ANSWER = '';

export const ComplianceFields: React.FC<IComplianceFieldsProps> = ({
    answers,
    answer,
    comment,
    disabled,
    commentError,
    onAnswer,
    onComment,
}) => {
    const data = [
        { value: NO_ANSWER, label: 'Нет ответа' },
        ...answers.map((item) => ({ value: item.value, label: COMPLIANCE_LABEL[item.compliance] ?? item.value })),
    ];
    const options = data.some((item) => item.value === answer) ? data : [...data, { value: answer, label: answer }];
    const required = commentRequiredFor(answers, answer);
    return (
        <>
            <FormGroup label="Соответствие ТЗ / спецификации">
                <Select
                    aria-label="Соответствие ТЗ"
                    options={options}
                    values={options.filter((item) => item.value === answer)}
                    disabled={disabled}
                    readOnly
                    onChange={([option]) => option && onAnswer(option.value)}
                />
            </FormGroup>
            {!answers.some((item) => item.value === answer) && answer !== NO_ANSWER && (
                <Hint
                    title={`Сохранён ответ «${answer}», которого нет среди вариантов`}
                    tone="warning"
                    maxWidth="100%"
                />
            )}
            <FormGroup label="Комментарий" required={required} invalid={!!commentError} errorMessage={commentError}>
                <Textarea
                    value={comment}
                    disabled={disabled}
                    autoResize
                    initialRowCount={3}
                    invalid={!!commentError}
                    onChange={(event) => onComment(event.target.value)}
                    aria-label="Комментарий к ответу по ТЗ"
                />
            </FormGroup>
        </>
    );
};

interface IApplyToSupplierOffersProps {
    /** Сколько ещё предложений поставщика получат те же сведения. */
    count: number;
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
}

/** Отметка группового применения; без других доступных предложений поставщика не показывается. */
export const ApplyToSupplierOffers: React.FC<IApplyToSupplierOffersProps> = ({ count, checked, disabled, onChange }) =>
    count > 0 ? (
        <Checkbox
            className="ws2-dialog__apply-all"
            label="Применить ко всем предложениям поставщика"
            description={supplierOffersHint(count)}
            checked={checked}
            disabled={disabled}
            onCheckedChange={(checked) => onChange(checked)}
        />
    ) : null;
